import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ouvir, usar, obterCanalAberto } from '../lib/chatEquipeStream';
import ChamadaEquipe from './ChamadaEquipe';
import '../pages/escritorio/ChatEquipe.css';

import Icone from './Icone';
import AvisoAgenda from './AvisoAgenda';
/**
 * Aviso de mensagem nova do chat da equipe, em QUALQUER tela do cockpit.
 *
 * Mensagem de outra pessoa numa conversa que não está aberta na tela (ou com a
 * aba em segundo plano) → um toque curto, o cartão no canto e, se a pessoa
 * permitiu, a notificação do sistema. Conversa silenciada não toca, a não ser
 * que a mensagem mencione a pessoa. Também publica o total de não lidas pro
 * número do menu (evento `chat-equipe-naolidas`).
 */
function tocar() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1175, ctx.currentTime + 0.09);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.34);
    setTimeout(() => { try { ctx.close(); } catch (_) { /* ok */ } }, 600);
  } catch (_) { /* sem som, sem problema */ }
}

function meuId() {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.id || null; } catch (_) { return null; }
}

export function somLigado() {
  try { return localStorage.getItem('chat_equipe_som') !== 'off'; } catch (_) { return true; }
}

function ehParceiro() {
  try { const p = JSON.parse(localStorage.getItem('usuario') || 'null')?.papel; return p === 'parceiro_societario' || p === 'primeiro_contato'; } catch (_) { return false; }
}

export default function ChatEquipeNotificador() {
  return ehParceiro() ? null : <NotificadorInterno />;
}

function NotificadorInterno() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [cartoes, setCartoes] = useState([]);
  const silenciados = useRef(new Set());
  const naChat = pathname.startsWith('/escritorio/chat');
  const naChatRef = useRef(naChat);
  naChatRef.current = naChat;

  const atualizarContagem = useCallback(() => {
    api.get('/equipe-chat/nao-lidas')
      .then(({ data }) => window.dispatchEvent(new CustomEvent('chat-equipe-naolidas', { detail: data || { total: 0 } })))
      .catch(() => { /* número do menu é enfeite */ });
  }, []);
  const atualizarSilenciados = useCallback(() => {
    api.get('/equipe-chat/canais')
      .then(({ data }) => { silenciados.current = new Set((data?.canais || []).filter((c) => c.silenciado).map((c) => c.id)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const soltar = usar();
    atualizarContagem();
    atualizarSilenciados();
    let t = null;
    const recontar = () => { clearTimeout(t); t = setTimeout(atualizarContagem, 400); };
    const offs = [
      ouvir('pronto', recontar),
      ouvir('lido', recontar),
      ouvir('canal', () => { recontar(); atualizarSilenciados(); }),
      ouvir('mensagem', (d) => {
        recontar();
        const m = d?.mensagem;
        const eu = meuId();
        if (!m || m.tipo === 'sistema' || m.autor_id === eu) return;
        const mencionado = (m.mencoes || []).includes(eu);
        if (silenciados.current.has(d.canal_id) && !mencionado) return;
        const visivel = document.visibilityState === 'visible';
        if (visivel && naChatRef.current && obterCanalAberto() === d.canal_id) return;
        const titulo = d.canal_nome ? `${m.autor_nome} · ${d.canal_nome}` : m.autor_nome;
        const corpo = m.tipo === 'imagem' ? '📷 Imagem' : m.tipo === 'audio' ? '🎤 Áudio'
          : m.tipo === 'arquivo' ? `📎 ${m.anexo?.nome || 'Arquivo'}` : String(m.texto || '').slice(0, 140);
        if (somLigado()) tocar();
        if (!visivel && typeof window.Notification !== 'undefined' && window.Notification.permission === 'granted') {
          try {
            const n = new window.Notification(titulo || 'Chat da equipe', { body: corpo, tag: `chat-${d.canal_id}`, icon: '/logo192.png' });
            n.onclick = () => { window.focus(); navigate(`/escritorio/chat?c=${d.canal_id}`); n.close(); };
          } catch (_) { /* navegador sem suporte */ }
        }
        if (!naChatRef.current || obterCanalAberto() !== d.canal_id) {
          const id = `${d.canal_id}-${m.id}`;
          setCartoes((cs) => [...cs.filter((c) => c.canal_id !== d.canal_id), { id, canal_id: d.canal_id, titulo, corpo, mencionado }].slice(-3));
          setTimeout(() => setCartoes((cs) => cs.filter((c) => c.id !== id)), 7000);
        }
      }),
    ];
    return () => { offs.forEach((f) => f()); clearTimeout(t); soltar(); };
  }, [atualizarContagem, atualizarSilenciados, navigate]);

  return (
    <>
    <ChamadaEquipe />
    <AvisoAgenda />
    {cartoes.length > 0 && (
    <div className="ceq-avisos" aria-live="polite">
      {cartoes.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`ceq-aviso${c.mencionado ? ' ceq-aviso-mencao' : ''}`}
          onClick={() => { setCartoes((cs) => cs.filter((x) => x.id !== c.id)); navigate(`/escritorio/chat?c=${c.canal_id}`); }}
        >
          <span className="ceq-aviso-titulo"><Icone nome="mensagem" className="icone-txt" /> {c.titulo}</span>
          <span className="ceq-aviso-corpo">{c.corpo}</span>
        </button>
      ))}
    </div>
    )}
    </>
  );
}
